import { useCallback, useState } from 'react';
import { appeler, ErreurApi } from './api';

/**
 * CAPACITÉ ET POUVOIRS (§7) — QUI PEUT SIGNER, ET À QUELLE DATE.
 *
 * ⚠️⚠️ LA DATE EST LE CŒUR DE CET ÉCRAN, pas une commodité. La même procuration
 * est valable pour l'acte de mars et caduque pour celui de septembre. Un écran
 * qui ne répondrait que pour « aujourd'hui » donnerait la mauvaise réponse à
 * quiconque instruit un acte daté — c'est-à-dire à tout le monde, puisqu'un
 * notaire régularise aussi des actes anciens. Le champ de date est donc en
 * tête, visible, et son changement relance la mesure.
 *
 * ⚠️ « INCONNUE » N'EST PAS « CAPABLE », ET L'ÉCRAN LES SÉPARE VISUELLEMENT.
 * Une personne sur laquelle le registre ne détient aucune mesure PEUT agir —
 * mais l'ignorance se dit. Les confondre ferait passer « je n'ai rien vérifié »
 * pour « j'ai vérifié, tout va bien », et c'est exactement le vert obtenu faute
 * d'avoir trouvé quoi contredire.
 *
 * ⚠️ AUCUNE RÈGLE MÉTIER ICI. L'empêchement, le représentant valable et la
 * portée du pouvoir sont calculés par `fon.pouvoir_d_agir` (migration 150), en
 * base. Recopier la règle dans l'écran en ferait une seconde, qui divergerait
 * au premier correctif appliqué d'un seul côté — le motif le plus coûteux de ce
 * dépôt. L'écran demande, il ne récite pas.
 */

interface Personne {
  id: string; denomination: string; type_personne?: string; type?: string;
  identite_verifiee: boolean; nin_quatre_derniers?: string | null;
}
interface Pouvoir {
  etat: string; agit_seul: boolean; assiste: boolean; qualite: string | null;
  representant: Personne | null; empechement: string | null; mesure: string | null;
}
interface LigneCapacite {
  id: string; etat: string; valide_du: string; valide_au: string | null;
  source: string; autorite: string | null; reference_piece: string | null; motif: string | null;
}
interface LigneRepresentation {
  id: string; qualite: string; valide_du: string; valide_au: string | null;
  statut: string; assiste: boolean; actes_autorises: string[] | null;
  plafond_valeur: string | null; source: string; autorite: string | null;
  reference_piece: string | null; motif_revocation: string | null; representant: string;
}
interface Reponse {
  personne: Personne; apprecie_le: string; pouvoir: Pouvoir;
  capacites: LigneCapacite[]; representations: LigneRepresentation[];
}

/**
 * ⚠️ CES LIBELLÉS DISENT LA CONSÉQUENCE, PAS SEULEMENT L'ÉTAT. « tutelle » ne
 * dit rien à qui ne connaît pas le droit ; « le tuteur agit à sa place » le
 * dit. Sur un poste d'État, l'étiquette doit suffire à décider.
 */
const ETATS: Record<string, { libelle: string; consequence: string; grave: boolean }> = {
  capable:       { libelle: 'Capable',              consequence: 'Agit seule et signe elle-même.', grave: false },
  inconnue:      { libelle: 'Non mesurée',          consequence: "Le registre ne détient aucune mesure. Ce n'est PAS une attestation de capacité.", grave: false },
  mineur:        { libelle: 'Mineur',               consequence: 'Le représentant légal agit à sa place.', grave: true },
  tutelle:       { libelle: 'Sous tutelle',         consequence: 'Le tuteur agit À LA PLACE de la personne.', grave: true },
  curatelle:     { libelle: 'Sous curatelle',       consequence: 'Le curateur agit AVEC la personne : les DEUX signent.', grave: true },
  sauvegarde:    { libelle: 'Sauvegarde de justice', consequence: 'Mesure de protection en cours.', grave: true },
  interdiction:  { libelle: 'Interdiction',         consequence: 'La personne ne peut pas disposer.', grave: true },
};

const QUALITES: Record<string, string> = {
  procuration: 'Procuration (pouvoir spécial)', mandat: 'Mandat (pouvoir général)',
  organe: "Organe d'une personne morale", legale: 'Représentation légale',
  tutelle: 'Tutelle', curatelle: 'Curatelle', delegation: 'Délégation',
  succession: 'Indivision successorale',
};

/** ⚠️ Un pouvoir « sur déclaration » n'est pas un pouvoir prouvé : on le DIT. */
const SOURCES: Record<string, { libelle: string; prouve: boolean }> = {
  acte_depose: { libelle: 'Acte déposé', prouve: true },
  jugement:    { libelle: 'Jugement', prouve: true },
  statuts:     { libelle: 'Statuts', prouve: true },
  etat_civil:  { libelle: "État civil", prouve: true },
  registre:    { libelle: 'Registre', prouve: true },
  declaration: { libelle: 'Sur déclaration', prouve: false },
};

const jour = (v: string | null) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

export function Capacite() {
  const [q, setQ] = useState('');
  const [trouves, setTrouves] = useState<Personne[] | null>(null);
  const [reponse, setReponse] = useState<Reponse | null>(null);
  const [date, setDate] = useState('');
  const [acte, setActe] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const chercher = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setErreur(null); setChargement(true); setReponse(null);
    try {
      const r = await appeler<{ titulaires: Personne[]; message?: string }>(
        `/foncier/titulaires?q=${encodeURIComponent(q.trim())}`);
      setTrouves(r.titulaires); setMessage(r.message ?? null);
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : String((x as Error)?.message ?? x));
    } finally { setChargement(false); }
  }, [q]);

  const mesurer = useCallback(async (id: string, quand = date, pourActe = acte) => {
    setErreur(null); setChargement(true);
    try {
      const p = new URLSearchParams();
      // ⚠️ `date` VIDE = aujourd'hui, et on ne l'envoie PAS : envoyer une chaîne
      // vide ferait échouer la validation `datetime()` du service, et l'écran
      // afficherait « requête invalide » pour un champ que l'agent n'a pas
      // rempli — c'est-à-dire un reproche pour une absence légitime.
      if (quand) p.set('date', new Date(quand).toISOString());
      if (pourActe) p.set('acte', pourActe);
      setReponse(await appeler<Reponse>(`/capacite/${id}${p.toString() ? `?${p}` : ''}`));
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : String((x as Error)?.message ?? x));
    } finally { setChargement(false); }
  }, [date, acte]);

  const etat = reponse ? (ETATS[reponse.pouvoir.etat] ?? ETATS.inconnue!) : null;

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2>Capacité et pouvoirs</h2>
        <p className="mention">
          Qui peut signer, en quelle qualité, et <strong>à quelle date</strong>. La chaîne du §7 :
          personne → qualité → pouvoir → validité → acte. Le registre répond pour la date de
          l'acte, jamais pour « aujourd'hui » : une procuration valable en mars peut être caduque
          en septembre.
        </p>
      </div>

      <form className="filtres" onSubmit={chercher}>
        <label>Nom de la personne
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="CAMARA, Aïssatou Bah…" />
        </label>
        <button className="bouton" disabled={q.trim().length < 2}>Chercher</button>
      </form>

      {erreur && <p className="erreur">{erreur}</p>}
      {chargement && <p className="attente">Lecture du registre…</p>}
      {message && !chargement && <p className="mention">{message}</p>}

      {trouves && trouves.length > 0 && !reponse && (
        <table className="tableau">
          <thead><tr><th>Personne</th><th>Identité</th><th /></tr></thead>
          <tbody>
            {trouves.map((t) => (
              <tr key={t.id}>
                <td><strong>{t.denomination}</strong></td>
                <td>{t.identite_verifiee
                  ? 'Vérifiée'
                  /* ⚠️ « non vérifiée » ≠ « fausse » : on ne juge pas, on dit l'état du registre. */
                  : <span className="mention">Non vérifiée au registre</span>}</td>
                <td><button className="bouton" onClick={() => void mesurer(t.id)}>
                  Examiner le pouvoir d'agir
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {reponse && (
        <>
          <h3 className="titre-section">{reponse.personne.denomination}</h3>

          <form className="filtres" onSubmit={(e) => {
            e.preventDefault(); void mesurer(reponse.personne.id);
          }}>
            <label>À la date de l'acte
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label>Pour l'acte
              <select value={acte} onChange={(e) => setActe(e.target.value)}>
                <option value="">Tout acte</option>
                <option value="vente">Vente</option>
                <option value="donation">Donation</option>
                <option value="succession">Succession</option>
                <option value="echange">Échange</option>
                <option value="inscription_surete">Inscription de sûreté</option>
              </select>
            </label>
            <button className="bouton">Mesurer</button>
            <button type="button" className="bouton discret"
                    onClick={() => { setReponse(null); setDate(''); setActe(''); }}>
              Changer de personne
            </button>
          </form>

          <p className="mention">
            Apprécié au {new Date(reponse.apprecie_le).toLocaleString('fr-FR')}
            {acte ? ` · pour un acte de type « ${acte} »` : ' · tous actes confondus'}
          </p>

          {/* ⚠️ L'EMPÊCHEMENT EST LA PREMIÈRE CHOSE LUE, avant l'état : c'est ce
              qui décide si l'acte peut être reçu. Le reste est du contexte. */}
          {reponse.pouvoir.empechement && (
            <p className="erreur"><strong>L'acte ne peut pas être reçu.</strong>{' '}
              {reponse.pouvoir.empechement}</p>
          )}

          <table className="tableau">
            <tbody>
              <tr>
                <th>État de capacité</th>
                <td>
                  <strong>{etat!.libelle}</strong>
                  <br /><span className="mention">{etat!.consequence}</span>
                </td>
              </tr>
              <tr>
                <th>Agit seule</th>
                <td>{reponse.pouvoir.agit_seul ? 'Oui' : 'Non'}</td>
              </tr>
              <tr>
                <th>Représentée par</th>
                <td>{reponse.pouvoir.representant
                  ? <>
                      <strong>{reponse.pouvoir.representant.denomination}</strong>
                      {' — '}{QUALITES[reponse.pouvoir.qualite ?? ''] ?? reponse.pouvoir.qualite}
                      {/* ⚠️ LA CURATELLE EXIGE DEUX SIGNATURES. Un acte portant la
                          seule signature du curateur est NUL : la mention doit
                          être impossible à manquer. */}
                      {reponse.pouvoir.assiste && (
                        <><br /><span className="erreur">
                          Curatelle : le représenté <strong>et</strong> le curateur signent tous les deux.
                        </span></>
                      )}
                    </>
                  : <span className="mention">
                      Personne — {reponse.pouvoir.agit_seul
                        ? 'elle agit pour elle-même.'
                        : "et aucun représentant n'est valable à cette date."}
                    </span>}
                </td>
              </tr>
            </tbody>
          </table>

          {reponse.pouvoir.mesure && <p className="mention">{reponse.pouvoir.mesure}</p>}

          <h3 className="titre-section">Mesures de capacité inscrites</h3>
          {reponse.capacites.length === 0
            ? <p className="mention">
                Aucune mesure inscrite. Le registre ne dit donc rien de la capacité de cette
                personne — ni qu'elle est capable, ni qu'elle ne l'est pas.
              </p>
            : <table className="tableau">
                <thead><tr><th>État</th><th>Du</th><th>Au</th><th>Source</th><th>Référence</th></tr></thead>
                <tbody>
                  {reponse.capacites.map((c) => (
                    <tr key={c.id}>
                      <td>{ETATS[c.etat]?.libelle ?? c.etat}</td>
                      <td>{jour(c.valide_du)}</td>
                      <td>{c.valide_au ? jour(c.valide_au) : <span className="mention">en cours</span>}</td>
                      <td>{SOURCES[c.source]?.libelle ?? c.source}
                        {SOURCES[c.source]?.prouve === false &&
                          <><br /><span className="mention">non prouvée par une pièce</span></>}</td>
                      <td>{c.reference_piece ?? '—'}{c.autorite ? <><br /><span className="mention">{c.autorite}</span></> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}

          <h3 className="titre-section">Pouvoirs donnés</h3>
          {reponse.representations.length === 0
            ? <p className="mention">Aucun pouvoir inscrit au nom de cette personne.</p>
            : <table className="tableau">
                <thead><tr>
                  <th>Représentant</th><th>Qualité</th><th>Portée</th>
                  <th>Du</th><th>Au</th><th>Statut</th>
                </tr></thead>
                <tbody>
                  {reponse.representations.map((r) => (
                    <tr key={r.id}>
                      <td><strong>{r.representant}</strong></td>
                      <td>{QUALITES[r.qualite] ?? r.qualite}
                        {r.assiste && <><br /><span className="mention">assistance : les deux signent</span></>}</td>
                      <td>{/* ⚠️ « TOUT ACTE » EST LE CAS DANGEREUX, et il se voit.
                               `null` = pouvoir général. Un tableau vide, lui, ne
                               couvrirait RIEN — les deux se ressemblent en base et
                               ne se ressemblent pas en droit. */
                        r.actes_autorises === null
                          ? <span className="erreur">Tout acte</span>
                          : r.actes_autorises.join(', ')}
                        {r.plafond_valeur && <><br /><span className="mention">plafond {r.plafond_valeur}</span></>}</td>
                      <td>{jour(r.valide_du)}</td>
                      <td>{r.valide_au ? jour(r.valide_au) : <span className="mention">sans terme</span>}</td>
                      <td>{r.statut === 'active' ? 'Actif' : (
                        <>{r.statut}{r.motif_revocation
                          ? <><br /><span className="mention">{r.motif_revocation}</span></>
                          : null}</>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </>
      )}
    </div>
  );
}
